// StrictMode removed to prevent double auth events
import './debug/forceConsole'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.jsx'
import { AuthProvider } from './features/auth'
import { NotificationListenerProvider } from './features/notifications/context/NotificationListenerContext'
import * as Sentry from '@sentry/react'

const onNetlify = typeof window !== 'undefined' && (
  /\.netlify\.app$/.test(window.location.hostname) ||
  window.location.hostname === 'flexwise.io' ||
  window.location.hostname === 'www.flexwise.io'
)
const isFirefox = typeof navigator !== 'undefined' && /firefox/i.test(navigator.userAgent || '');
const REPLAYS_SESSION = Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_SESSION ?? '0');
const REPLAYS_ERROR = Number.parseFloat(import.meta.env.VITE_SENTRY_REPLAYS_ERROR ?? '0.1');
const useReplayIntegration = (REPLAYS_SESSION > 0 || REPLAYS_ERROR > 0);

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN || 'https://a9349926c696b4ca3148da5d708abfd2@o4510102136815616.ingest.de.sentry.io/4510102219784272',
  enabled: import.meta.env.PROD, // Only enable Sentry in production
  tunnel: onNetlify ? '/.netlify/functions/sentry' : undefined,
  environment: import.meta.env.MODE,
  sendDefaultPii: false,
  tracesSampleRate: 0.2,
  profilesSampleRate: 0.1,
  replaysSessionSampleRate: REPLAYS_SESSION,
  replaysOnErrorSampleRate: REPLAYS_ERROR,
  attachStacktrace: true,
  allowUrls: [
    new RegExp(typeof window !== 'undefined' ? window.location.hostname.replace(/\./g, '\\.') : '.*'),
    /flexwise\.io/,
    /supabase\.co/,
    /fly\.dev/,
    /netlify\.app/
  ],
  denyUrls: [/extensions\//, /^chrome:\/\//, /^chrome-extension:\/\//],
  // Benign / environmental: SW blocked, stale chunks after deploy, React lazy internals
  ignoreErrors: [
    'Failed to register a ServiceWorker',
    'An unknown error occurred when fetching the script',
    'Cannot update a null/nonexistent service worker registration',
    'Script https://flexwise.io/sw.js load failed',
    "evaluating 'e._result.default'",
    'e._result is undefined',
    "can't access property \"default\", e._result is undefined",
  ],
  beforeSend(event) {
    try {
      if (event?.user) {
        delete event.user.email;
        delete event.user.ip_address;
        delete event.user.username;
      }
      if (event?.request) {
        delete event.request.headers;
        delete event.request.cookies;
      }
      const msg = [
        event?.message,
        ...(event?.exception?.values || []).map((v) => v?.value),
      ]
        .filter(Boolean)
        .join(' ')
      if (
        /service\s*worker|sw\.js|nonexistent service worker/i.test(msg) ||
        /Failed to fetch dynamically imported module|Loading chunk .* failed/i.test(msg) ||
        /Unexpected token '<'|expected expression, got '<'/i.test(msg)
      ) {
        return null
      }
    } catch { }
    return event;
  },
  integrations: [
    Sentry.browserTracingIntegration(),
    ...(useReplayIntegration ? [Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true })] : []),
    Sentry.feedbackIntegration({
      colorScheme: 'system',
      showBranding: false,
      formTitle: 'Bug melden',
      submitButtonLabel: 'Senden',
      messageLabel: 'Beschreibung',
      messagePlaceholder: 'Was ist passiert? Was haben Sie erwartet?',
      nameLabel: 'Name',
      namePlaceholder: 'Ihr Name (optional)',
      emailLabel: 'E-Mail',
      emailPlaceholder: 'Ihre E-Mail (optional)',
      cancelButtonLabel: 'Abbrechen',
      backButtonLabel: 'Zurück',
      enableScreenshot: !isFirefox,
      addScreenshotButtonLabel: 'Screenshot hinzufügen',
      removeScreenshotButtonLabel: 'Screenshot entfernen'
    })
  ]
})

// One-time connectivity test in dev/staging
try {
  const onStaging = typeof window !== 'undefined' && (window.location.hostname.includes('.fly.dev') || window.location.hostname.includes('localhost'))
  if (import.meta.env.DEV || onStaging) {
    Sentry.captureMessage('FlexWise Sentry connectivity test', 'info')
    // Ensure delivery of the log message
    try { await Sentry.flush(1500) } catch { }

    // Also emit a one-time test error to create the first Issue
    const flagKey = 'sentry_first_error_sent_v1'
    if (typeof window !== 'undefined' && !localStorage.getItem(flagKey)) {
      Sentry.captureException(new Error('FlexWise Sentry first error test'))
      try { await Sentry.flush(3000) } catch { }
      try { localStorage.setItem(flagKey, '1') } catch { }
    }
  }
} catch { }

// Manual debug trigger: append ?sentryDebug=1 to URL to force a capture in any env
try {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search)
    if (params.get('sentryDebug') === '1') {
      const marker = (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : String(Date.now())
      Sentry.captureMessage(`FlexWise Sentry manual debug ping ${marker}`, 'info')
      try { await Sentry.flush(1500) } catch { }
      Sentry.captureException(new Error(`FlexWise Sentry manual debug exception ${marker}`))
      try { await Sentry.flush(3000) } catch { }
    }
  }
} catch { }

// Suppress AuthSessionMissingError unhandled rejections
// This edge case occurs during realtime reconnection when auth is in a transitional state
// See: https://github.com/supabase/auth-js - expected behavior during session refresh race conditions
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    if (reason?.name === 'AuthSessionMissingError' || reason?.message?.includes('Auth session missing')) {
      console.warn('[Auth] Session temporarily unavailable during auth state transition');
      event.preventDefault(); // Prevent Sentry capture for this expected edge case
      return
    }
    // PostgREST/Supabase client sometimes rejects with a plain { code, message, ... } object.
    // Those are usually surfaced in UI via RTK; if one leaks as unhandled, avoid noisy Sentry events.
    if (
      reason &&
      typeof reason === 'object' &&
      !Array.isArray(reason) &&
      !(reason instanceof Error) &&
      typeof reason.message === 'string' &&
      reason.code !== undefined
    ) {
      console.warn('[unhandledrejection] API error object (suppressed):', reason.message || reason)
      event.preventDefault()
    }
  });

  // Auto-reload on chunk preload failures (CSS/JS preloads after deploy)
  window.addEventListener('vite:preloadError', (event) => {
    const k = '__vite_preload_reload';
    const tried = sessionStorage.getItem(k);
    if (!tried) {
      try { sessionStorage.setItem(k, '1'); } catch {}
      event.preventDefault();
      // Unregister stale SW so reload gets fresh index.html from server
      if ('serviceWorker' in navigator && navigator.serviceWorker.getRegistrations) {
        navigator.serviceWorker.getRegistrations()
          .then((regs) => {
            for (const r of (regs || [])) r.unregister();
          })
          .catch(() => {})
          .finally(() => {
            window.location.reload();
          });
      } else {
        // Service workers not supported; just reload to recover
        window.location.reload();
      }
      return;
    }
    // Second attempt failed — clear flags so user can try again manually
    try { sessionStorage.removeItem(k); } catch {}
  });
}

// ── Early notification intent capture ───────────────────────────────────────
// Must run BEFORE React mounts. When the SW opens a cold-start URL with
// ?from=notification, capture the intent immediately so it survives the
// 2-3s auth loading delay. NotificationBellIndicator reads this on mount.
try {
  const _p = new URLSearchParams(window.location.search)
  if (_p.get('from') === 'notification') {
    // Preserve the original URL with date param but without notification-specific params
    const eventType = _p.get('event_type') || 'substitution.published'
    const eventKey = _p.get('event_key') || ''
    const dateParam = _p.get('date') || null
    const queueId = _p.get('queue_id') || null
    _p.delete('from')
    _p.delete('event_type')
    _p.delete('event_key')
    _p.delete('queue_id')
    const cleanSearch = _p.toString() ? `?${_p.toString()}` : ''
    window.__fw_pending_notification = {
      url: window.location.pathname + cleanSearch,
      event_type: eventType,
      event_key: eventKey,
      date: dateParam,
      queue_id: queueId,
    }
    // Clean URL in address bar
    window.history.replaceState({}, '', window.location.pathname + cleanSearch + window.location.hash)
  }
} catch { /* safety */ }

// Register service worker for PWA + push notification functionality.
// ⚠️ Dev mode SW: caching can serve stale code after HMR. Use hard refresh
//    (Cmd+Shift+R) or enable "Update on reload" in DevTools > Application > SW
//    if you see stale behavior during development.
const isProduction = import.meta.env.PROD
const isStaging = window.location.hostname.includes('.fly.dev') || window.location.hostname.includes('staging')
const isDevelopment = import.meta.env.DEV
// Register SW in production, dev, and deploy previews (needed for push subscriptions)
const shouldRegisterSW = isProduction || isDevelopment || /\.netlify\.app$/.test(typeof window !== 'undefined' ? window.location.hostname : '')





const canRequestNotifications = typeof window !== 'undefined'
  && 'Notification' in window
  && typeof Notification.requestPermission === 'function'

const requestNotificationPermission = () => {
  if (!canRequestNotifications) return
  if (Notification.permission !== 'default') return
  setTimeout(() => {
    try {
      const maybePromise = Notification.requestPermission()
      // Older browsers return a string directly instead of a Promise
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.catch((err) => {
          console.warn('Notification permission request failed', err)
        })
      }
    } catch (err) {
      console.warn('Notification permission request threw', err)
    }
  }, 2000)
}

const registerServiceWorker = async () => {
  try {
    const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' })

    try {
      // Check for updates, but do not auto-activate or reload.
      void registration.update().catch(() => { /* benign: offline, blocked, or no pending update */ })

      // Notify the app when a new version is ready (installed and there is an active controller)
      registration.addEventListener('updatefound', () => {
        const newSW = registration.installing
        if (!newSW) return
        newSW.addEventListener('statechange', () => {
          if (newSW.state === 'installed' && navigator.serviceWorker.controller) {
            // Dispatch a custom event so the React app can show a prompt
            window.dispatchEvent(new CustomEvent('sw-update-available', { detail: { registration } }))
          }
        })
      })

      // Only reload when user explicitly accepts the update
      let updatingByUser = false
      window.addEventListener('sw-accept-update', () => { updatingByUser = true })
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (updatingByUser) {
          // No full-page reload; the app will continue running with updated assets
          console.info('Service worker activated; skipping forced reload')
        }
      })
    } catch (e) {
      console.warn('SW update flow warning', e)
    }

    requestNotificationPermission()
  } catch (registrationError) {
    // Ad blockers, offline, or CDN hiccups — PWA still works without SW; do not spam Sentry
    console.warn('SW registration failed:', registrationError)
  }
}

if ('serviceWorker' in navigator && shouldRegisterSW) {
  window.addEventListener('load', () => {
    registerServiceWorker()
  })
}

function ErrorFallback() {
  const reload = () => { try { window.location.reload(); } catch { } };
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="max-w-md w-full bg-white shadow-sm border rounded-xl p-6 text-center">
        <img src="/error-fallback.svg" alt="Fehler Illustration" className="mx-auto h-24 mb-4 opacity-90" />
        <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center">!</div>
        <h1 className="text-lg font-semibold mb-1">Hups, da ist etwas schiefgelaufen.</h1>
        <p className="text-sm text-gray-600 mb-4">Unser Tech‑Team wurde automatisch informiert. Bitte versuch es erneut.</p>
        <div className="flex items-center justify-center gap-2">
          <button onClick={reload} className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800">Neu laden</button>
          <a href="/" className="px-3 py-1.5 rounded-md border text-sm hover:bg-gray-50">Zur Startseite</a>
        </div>
      </div>
    </div>
  );
}

const params = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
const forceErrorScreen = params.get('showError') === '1';

import { Provider } from 'react-redux'
import { store } from './store'

createRoot(document.getElementById('root')).render(
  <Provider store={store}>
    <AuthProvider>
      {/*
        Single-mount listener for Realtime channels + SW push messages.
        Lives outside <App /> (and therefore outside the Router) so route
        changes don't remount the hook and re-open Realtime channels —
        that was a major source of `realtime.subscription` row leakage
        when NotificationBellIndicator was rendered in both Header and
        Navigation simultaneously.
      */}
      <NotificationListenerProvider>
        {forceErrorScreen ? (
          <ErrorFallback />
        ) : (
          <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
            <App />
          </Sentry.ErrorBoundary>
        )}
      </NotificationListenerProvider>
    </AuthProvider>
  </Provider>
)
